import { useSyncExternalStore } from 'react';
import { readUiPreferences, subscribeUiPreferences, writeUiPreferences } from './preferences.js';
import { japanese } from './japanese.js';
import { additionalCopy } from './additional-copy.js';

/**
 * Shared chrome is written in Chinese; English looks each Chinese source string
 * up here. Names follow the shared English glossary used by Account and the
 * products: 交汇地 Crossroads, 档案 Archive, 工房 Workshop, 市集 Market,
 * 营地 Camp, 发现 Discover, 系列 Series, 刻印 Imprint,
 * 会馆 Guild Hall, 兔子洞 Rabbit Hole, 公告栏 Bulletin, 购物袋 Bag.
 */
const english = {
  '交汇地': 'Crossroads',
  '交汇地首页': 'Crossroads home',
  '交汇地全局导航': 'Crossroads navigation',
  '交汇地主导航': 'Crossroads main navigation',
  '发现': 'Discover',
  '关注': 'Following',
  '切换信息流': 'Switch feed',
  '切换信息流，当前：{name}': 'Switch feed, current: {name}',
  '营地': 'Camp',
  '返回营地': 'Back to camp',
  '兔子洞': 'Rabbit Hole',
  '关闭兔子洞': 'Close Rabbit Hole',
  '公告栏': 'Bulletin',
  '创作': 'Create',
  '购物袋': 'Bag',
  '更多入口': 'More',
  '工房': 'Workshop',
  '工房导航': 'Workshop navigation',
  '当前系列': 'This series',
  '系列栏目': 'Series sections',
  '选择系列': 'Choose a series',
  '系列：{name}': 'Series: {name}',
  '未开发': 'Coming later',
  '前往系列': 'Go to the series',
  '在工房编辑': 'Edit in the Workshop',
  '当前页面': 'This page',
  '前往': 'Go to',
  '内容': 'Content',
  '搜索“{query}”': 'Search for “{query}”',
  '指令面板': 'Command menu',
  '前往页面、查找内容或执行操作…': 'Go to a page, find content or run an action…',
  '查找指令或内容': 'Find a command or content',
  '关闭指令面板': 'Close command menu',
  '可用指令': 'Available commands',
  '↑ ↓ 选择': '↑ ↓ Select',
  'Enter 打开': 'Enter Open',
  'Esc 关闭': 'Esc Close',
  '先建一个系列收好故事，也可以直接去工房动手。': 'Start a series to keep your story, or go straight to the Workshop.',
  '带我做一遍 →': 'Walk me through it →',
  '登录后即可开始创作。': 'Sign in to start creating.',
  '暂时无法完成，请重试。': "This couldn't be done. Try again.",
  '连接超时，请重试。': 'The connection timed out. Try again.',
  '请求超时，请重试。': 'The request timed out. Try again.',
  '未命名系列': 'Untitled series',
  '创建系列': 'Create a series',
  '系列已创建': 'Series created',
  '返回上一步': 'Back',
  '关闭创作': 'Close',
  '正在读取…': 'Loading…',
  '重试': 'Try again',
  '登录': 'Sign in',
  '发布作品': 'Publish a work',
  '发布周边': 'Publish goods',
  '上传并公开作品': 'Upload and share your work',
  '从设计开始': 'Start with a design',
  '管理作品与周边': 'Manage your works and goods',
  '选择作品的归属与来源': 'Choose where it belongs and where it comes from',
  '做周边': 'Make merch',
  '选择素材，进入周边设计': 'Choose an image and open the merch designer',
  '开始自己的世界': 'Start your own world',
  '系列名称': 'Series name',
  '取消': 'Cancel',
  '正在创建…': 'Creating…',
  '进入系列': 'Open the series',
  '粘贴坐标或朋友分享的内容，即可到达。': 'Paste a series address or something a friend shared to go there.',
  '系列坐标': 'Series address',
  '粘贴系列坐标': 'Paste a series address',
  '寻找中…': 'Finding…',
  '进入': 'Go',
  '没有找到可访问的系列，请检查坐标。': "No series you can open was found. Check the address.",
  '入口暂时无法连接，请重试。': "The entrance can't be reached right now. Try again.",
  '{name} 全局工具栏': '{name} toolbar',
  '返回 {name}': 'Back to {name}',
  '头像与档案': 'Avatar and profile',
  '关闭{title}': 'Close',
  '正在连接资料服务': 'Connecting to your profile…',
  '资料暂时无法加载': "Your profile can't be loaded right now.",
  '账户菜单': 'Account menu',
  '我的账户': 'My account',
  '个人资料': 'Profile',
  '魔素': 'Mana',
  '魔素与用量': 'Mana & usage',
  '个人事务': 'Settings',
  '外观': 'Appearance',
  '退出登录': 'Sign out',
  '正在退出…': 'Signing out…',
  '退出失败，请稍后重试': 'Could not sign out. Please try again.',
  '切换为简体中文': 'Switch to Chinese',
  '语言': 'Language',
};

/** "en-GB" → 'en', "zh-Hant" → 'zh'; the first supported language wins, otherwise English. */
export function browserLocale(languages = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]) {
  const list = Array.from(languages || []).filter(Boolean).map(String);
  for (const tag of list) {
    const value = tag.toLowerCase();
    if (value.startsWith('zh')) return 'zh';
    if (value.startsWith('en')) return 'en';
    if (value === 'ja' || value.startsWith('ja-')) return 'ja';
  }
  return list.length ? 'en' : 'zh';
}

/** The chosen language (shared cookie), otherwise the browser's. */
export function currentUiLocale() {
  if (typeof document === 'undefined') return 'zh';
  return readUiPreferences().locale || browserLocale();
}

const subscribe = listener => {
  if (typeof window === 'undefined') return () => {};
  return subscribeUiPreferences(() => listener());
};

export function useUiLocale() {
  return useSyncExternalStore(subscribe, currentUiLocale, () => 'zh');
}

export function translate(locale, chinese, values) {
  const template = locale === 'ja' ? additionalCopy[chinese]?.[1] ?? japanese[chinese] ?? english[chinese] ?? chinese : locale === 'en' ? additionalCopy[chinese]?.[0] ?? english[chinese] ?? chinese : chinese;
  return values ? template.replace(/\{(\w+)\}/g, (match, key) => key in values ? String(values[key]) : match) : template;
}

/** `t('发现')` in the current language; re-renders when the language changes. */
export function useUiText() {
  const locale = useUiLocale();
  return (chinese, values) => translate(locale, chinese, values);
}

export function setUiLocale(locale) {
  if (locale === 'zh' || locale === 'en' || locale === 'ja') writeUiPreferences({ locale });
}

/**
 * A link may carry ?lang=en (the English company site and invitations do).
 * Keep that choice for every RealRain surface and remove it from the address.
 */
export function adoptLanguageFromUrl(location = typeof window === 'undefined' ? undefined : window.location, history = typeof window === 'undefined' ? undefined : window.history) {
  if (!location) return;
  const url = new URL(location.href);
  const requested = url.searchParams.get('lang');
  if (requested !== 'en' && requested !== 'zh' && requested !== 'ja') return;
  setUiLocale(requested);
  url.searchParams.delete('lang');
  try { history?.replaceState(history.state, '', url.pathname + url.search + url.hash); } catch { /* The choice is kept either way. */ }
}

if (typeof window !== 'undefined') adoptLanguageFromUrl();
